import React, { useMemo, useState, useCallback } from 'react';
import { StyleSheet, View, Pressable, Platform, LayoutAnimation } from 'react-native';
import * as FileSystem from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import { useUIStore, useChatStore, useToastStore, useLightboxStore, useModalStore, useAuthStore } from '@/store';
import { MESSAGE_WIDTH_AMOUNTS, MESSAGE_LAYOUT } from '@/constants/config';
import Message from '@/components/Messages/Message';
import MessageResponseCard from '@/components/Messages/MessageResponseCard';
import MessageMediaCard from '@/components/Messages/MessageMediaCard';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import useResponsiveness from '@/hooks/useResponsiveness';
import copyText from '@/constants/utils/copy_text';
import markdownToPlainText from '@/constants/utils/markdown_to_plain_text';
import markdownToSpeechText from '@/constants/utils/markdown_to_speech_text';
import getMessageHtmlForCopy from '@/constants/utils/get_message_html_for_copy';
import { stripWorkInjectedPrompt } from '@/constants/work';
import PropTypes from 'prop-types';
import { isAwaitingRequestedMedia } from '@/constants/utils/message';
import useChatWaitingGame from '@/hooks/useChatWaitingGame';

// Helper to check if an image object has a valid URL
const hasValidImageUrl = (img) => {
  if (!img) return false;
  // Check various possible URL properties
  return !!(img.url || img.cdnPath || img.src || img.uri || img.imageUrl);
};

// Detect if a message is media-only (primarily images/video/audio with minimal text)
const isMediaOnlyResponse = (message) => {
  // Check for images - look for any valid URL property
  const hasImages = message.images?.length > 0 && message.images.some(hasValidImageUrl);
  
  // Also check for image / video / audio / 3D files
  const hasMediaFiles = message.files?.some(f => 
    ['image', 'video', 'audio', '3d'].includes(f.fileType) || 
    f.type?.startsWith?.('image/') ||
    f.type?.startsWith?.('video/') ||
    f.type?.startsWith?.('audio/') ||
    f.type === 'model/gltf-binary' ||
    f.type === 'model/gltf+json'
  );
  const hasMedia = hasImages || hasMediaFiles;
  
  // Check if this message is for media generation
  // isRequestingMedia is set when message is created (e.g., 'image', 'video', 'audio', '3d')
  // isImage is set when images arrive via stream
  // 'document' (PDF generation), 'presentation' (PPTX generation), and
  // 'spreadsheet' (XLSX generation) are intentionally EXCLUDED — the
  // assistant produces text + an attached artifact, which renders best in
  // MessageResponseCard (text + downloadable file card via MessageFiles)
  // rather than the media card.
  const isRequestingDocument = message.isRequestingMedia === 'document' ||
    message.isRequestingMedia === 'presentation' ||
    message.isRequestingMedia === 'spreadsheet';
  const isRequestingMedia = !!message.isRequestingMedia && !isRequestingDocument;
  const isImageModel = !!message.isImage;

  // If this is a media-generating response, treat as media-only regardless of text
  // This ensures image model responses show as media cards even with brief text
  if (isRequestingMedia || isImageModel) {
    return true;
  }
  
  // If the message has images, treat it as media-only
  // This catches cases where flags weren't set but images exist
  if (hasImages) {
    return true;
  }
  
  // For non-media-model responses with files, only treat as media-only if minimal text
  const textContent = message.content?.trim() || '';
  const hasMinimalText = textContent.length < 50;
  
  return hasMediaFiles && hasMinimalText;
};

// Check if any message in the group is still streaming
const hasStreamingMessage = (messages) => {
  return messages.some(m => m.isStreaming || m.isTyping || m.isWaiting);
};

// Create styles
const createStyles = (colors, isXS, messageWidthPixels) => StyleSheet.create({
  container: {
    width: '100%',
    marginTop: 8,
    marginBottom: isXS ? 16 : 8,
    paddingHorizontal: isXS ? 12 : 20,
  },
  innerContainer: {
    width: '100%',
    maxWidth: messageWidthPixels,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
    paddingHorizontal: 4,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  responsesBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: colors.solid_color_secondary,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  expandAllButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: colors.solid_color_secondary,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'background-color 0.15s ease',
    }),
  },
  expandAllButtonHovered: {
    backgroundColor: colors.solid_color_block,
  },
  cardsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  cardWrapper: {
    // Mobile: full width, Desktop: half width (accounting for gap)
    ...(Platform.OS === 'web'
      ? (
          Platform.OS === 'macos'
            ? { width: isXS ? '100%' : '48%' }
            : { width: isXS ? '100%' : 'calc(50% - 6px)' }
        )
      : { width: isXS ? '100%' : '48%' }
    ),
    minWidth: isXS ? undefined : 280,
  },
  cardWrapperFull: {
    width: '100%',
  },
  // For media cards, we want them smaller in a grid
  mediaCardWrapper: {
    ...(Platform.OS === 'web'
      ? (
          Platform.OS === 'macos'
            ? { width: isXS ? '50%' : '33.333%' }
            : { width: isXS ? 'calc(50% - 6px)' : 'calc(33.333% - 8px)' }
        )
      : { width: isXS ? '48%' : '31%' }
    ),
    minWidth: 140,
    maxWidth: isXS ? 180 : 220,
  },
});

function MessageGroup({
  messages,
  parentMessageId,
  conversationId,
  isOwner,
  chatTitle,
  isLastGroup = false,
}) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const messageWidth = useUIStore(state => state.messageWidth);
  const messageLayout = useUIStore(state => state.messageLayout);
  const { addToast } = useToastStore(state => state.actions);
  const { openLightbox } = useLightboxStore(state => state.actions);
  const { openModal } = useModalStore(state => state.actions);
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const canPlayWaitingGame = useChatWaitingGame(conversationId);
  
  // Track which cards are expanded (by message ID)
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [hoveredExpandAll, setHoveredExpandAll] = useState(false);

  const messageCount = messages.length;

  // Determine if all messages are media-only
  const allMediaOnly = useMemo(() => 
    messages.every(m => isMediaOnlyResponse(m)), 
    [messages]
  );

  // Determine if any message is streaming
  const isStreaming = useMemo(() => 
    hasStreamingMessage(messages), 
    [messages]
  );

  // Count text cards (non-media) for expand all logic
  const textCardCount = useMemo(() => 
    messages.filter(m => !isMediaOnlyResponse(m)).length, 
    [messages]
  );

  const allExpanded = expandedIds.size >= textCardCount && textCardCount > 0;

  // Convert messageWidth setting to pixel value
  const messageWidthPixels = useMemo(() => {
    const widthKey = messageWidth?.toUpperCase() || 'NARROW';
    const value = MESSAGE_WIDTH_AMOUNTS[widthKey];
    
    if (typeof value === 'string') {
      return '100%';
    }
    return value || 680;
  }, [messageWidth]);

  const styles = useMemo(() => 
    createStyles(colors, isXS, messageWidthPixels), 
    [colors, isXS, messageWidthPixels]
  );

  // Toggle a single card's expansion
  const toggleExpand = useCallback((messageId) => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  }, []);

  // Expand or collapse all text cards
  const toggleExpandAll = useCallback(() => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    if (allExpanded) {
      // Collapse all
      setExpandedIds(new Set());
    } else {
      // Expand all text cards
      const textIds = messages
        .filter(m => !isMediaOnlyResponse(m))
        .map(m => m.id);
      setExpandedIds(new Set(textIds));
    }
  }, [allExpanded, messages]);

  // Copy handler for a message
  const createCopyHandler = useCallback((message) => async () => {
    try {
      // Dual-payload copy: stripped plain text for plain destinations
      // (Notes, email, terminals) so they don't see raw ## or ** markers,
      // plus sanitized rendered HTML for rich destinations (Word, Google
      // Docs) and markdown editors that auto-convert HTML (Obsidian).
      const source = message.role === 'user'
        ? stripWorkInjectedPrompt(message.content)
        : message.content;
      const plainText = markdownToPlainText(source, { renderCards: message.role !== 'user' });
      const cleanHtml = getMessageHtmlForCopy(message.id, source);
      await copyText(plainText, cleanHtml);
      addToast(t('messages.copied'), 'success');
    } catch (error) {
      console.error('Failed to copy message:', error);
      addToast(t('messages.copyFailed'), 'error');
    }
  }, [addToast, t]);

  const createSpeakHandler = useCallback((message) => () => {
    const source = message.role === 'user'
      ? stripWorkInjectedPrompt(message.content || '')
      : (message.content || '');
    const plainText = markdownToSpeechText(source);
    if (!plainText?.trim()) {
      addToast(t('messages.speak.empty'), 'error');
      return;
    }
    if (isGhost) {
      openModal('GhostSignupModal');
      return;
    }
    openModal('MessageSpeakModal', { text: source });
  }, [addToast, isGhost, openModal, t]);

  // Bookmark handler
  const createBookmarkHandler = useCallback((message) => async () => {
    try {
      const isSaved = message?.flags?.isSaved === true;
      if (isSaved) {
        await useChatStore.getState().unsaveMessage(conversationId, message.id);
        addToast(t('messages.unsaved'), 'success');
      } else {
        await useChatStore.getState().saveMessage(conversationId, message.id);
        addToast(t('messages.saved'), 'success');
      }
    } catch (error) {
      console.error('Failed to toggle bookmark:', error);
      addToast(t('messages.bookmarkUpdateFailed'), 'error');
    }
  }, [conversationId, addToast, t]);

  // Preference handlers
  const createPreferHandler = useCallback((message) => async () => {
    try {
      await useChatStore.getState().setMessagePreference(conversationId, message.id, true);
      addToast(t('messages.markedPreferred'), 'success');
    } catch (error) {
      console.error('Failed to prefer response:', error);
      addToast(t('messages.markPreferredFailed'), 'error');
    }
  }, [conversationId, addToast, t]);

  const createRejectHandler = useCallback((message) => async () => {
    try {
      await useChatStore.getState().setMessagePreference(conversationId, message.id, false);
      addToast(t('messages.responseRejected'), 'success');
    } catch (error) {
      console.error('Failed to reject response:', error);
      addToast(t('messages.rejectFailed'), 'error');
    }
  }, [conversationId, addToast, t]);

  // Lightbox handler
  const handleOpenLightbox = useCallback((options) => {
    openLightbox({
      ...options,
      conversationId,
    });
  }, [openLightbox, conversationId]);

  // If only 1 message, render without grouping UI (expanded by default)
  if (messageCount === 1) {
    return (
      <Message
        conversationId={conversationId}
        isOwner={isOwner}
        chatTitle={chatTitle}
        message={messages[0]}
        isLastMessage={isLastGroup}
      />
    );
  }

  // If Slim layout, just render vertical list (no grouping UI)
  if (messageLayout === MESSAGE_LAYOUT.SLIM) {
    return (
      <React.Fragment>
        {messages.map((message, index) => (
          <Message
            key={message.id}
            conversationId={conversationId}
            isOwner={isOwner}
            chatTitle={chatTitle}
            message={message}
            isLastMessage={isLastGroup && index === messageCount - 1}
            isInMessageGroup={true}
            isLastGroupInChat={isLastGroup}
          />
        ))}
      </React.Fragment>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.innerContainer}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <View style={styles.responsesBadge}>
              <Icon name="layer-group" size={12} color={colors.text_color_tertiary} />
              <Text size="xs" weight="medium" color="tertiary">
                {t('messages.modelResponses').replace('%COUNT%', messageCount)}
              </Text>
            </View>
          </View>
          
          {/* Expand All / Collapse All button (only for text cards) */}
          {textCardCount > 1 && !isStreaming && (
            <View style={styles.headerActions}>
              <Pressable
                style={[
                  styles.expandAllButton,
                  hoveredExpandAll && styles.expandAllButtonHovered,
                ]}
                onPress={toggleExpandAll}
                onHoverIn={() => setHoveredExpandAll(true)}
                onHoverOut={() => setHoveredExpandAll(false)}
              >
                <Icon 
                  name={allExpanded ? 'compress' : 'expand'} 
                  size={12} 
                  color={colors.text_color_secondary} 
                />
                <Text size="xs" color="secondary">
                  {allExpanded ? t('messages.collapseAll') : t('messages.expandAll')}
                </Text>
              </Pressable>
            </View>
          )}
        </View>

        {/* Cards Grid */}
        <View style={styles.cardsGrid}>
          {messages.map((message) => {
            const isMedia = isMediaOnlyResponse(message);
            const isExpanded = expandedIds.has(message.id);
            
            if (isMedia) {
              // Media card - smaller in grid
              return (
                <View 
                  key={message.id} 
                  // A playable waiting card needs room for touch controls.
                  // Finished media returns to the compact thumbnail grid.
                  style={canPlayWaitingGame && isAwaitingRequestedMedia(message)
                    ? styles.cardWrapper
                    : allMediaOnly ? styles.mediaCardWrapper : styles.cardWrapperFull}
                >
                  <MessageMediaCard
                    message={message}
                    conversationId={conversationId}
                    onPreferResponse={isOwner ? createPreferHandler(message) : null}
                    onRejectResponse={isOwner ? createRejectHandler(message) : null}
                    isXS={isXS}
                  />
                </View>
              );
            }
            
            // Text card - expand/collapse behavior
            return (
              <View 
                key={message.id} 
                style={isExpanded ? styles.cardWrapperFull : styles.cardWrapper}
              >
                <MessageResponseCard
                  message={message}
                  conversationId={conversationId}
                  isExpanded={isExpanded}
                  onToggleExpand={() => toggleExpand(message.id)}
                  onCopy={createCopyHandler(message)}
                  onSpeak={createSpeakHandler(message)}
                  onBookmark={isOwner ? createBookmarkHandler(message) : null}
                  onExport={isOwner ? async (format) => {
                    // Simple text export
                    if (message.content) {
                      if (Platform.OS === 'web') {
                        const blob = new Blob([message.content], { type: 'text/plain' });
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `response-${message.id}.txt`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                        addToast(t('messages.exported'), 'success');
                      } else {
                        // Native export using file system and sharing
                        try {
                          const filename = `response-${message.id}.txt`;
                          const fileUri = `${FileSystem.documentDirectory}${filename}`;
                          await FileSystem.writeAsStringAsync(fileUri, message.content, {
                            encoding: FileSystem.EncodingType.UTF8,
                          });
                          const canShare = await Sharing.isAvailableAsync();
                          if (canShare) {
                            await Sharing.shareAsync(fileUri, {
                              mimeType: 'text/plain',
                              dialogTitle: t('messages.exportDialogTitle'),
                            });
                            addToast(t('messages.exported'), 'success');
                          } else {
                            // Fall back to copy if sharing not available
                            await copyText(message.content);
                            addToast(t('messages.copied'), 'success');
                          }
                        } catch (err) {
                          console.error('Export failed:', err);
                          addToast(t('messages.exportFailed'), 'error');
                        }
                      }
                    }
                  } : null}
                  onPreferResponse={isOwner ? createPreferHandler(message) : null}
                  onRejectResponse={isOwner ? createRejectHandler(message) : null}
                  onOpenLightbox={handleOpenLightbox}
                  isXS={isXS}
                />
              </View>
            );
          })}
        </View>
      </View>
    </View>
  );
}

MessageGroup.propTypes = {
  messages: PropTypes.arrayOf(PropTypes.object).isRequired,
  parentMessageId: PropTypes.string,
  conversationId: PropTypes.string.isRequired,
  isOwner: PropTypes.bool,
  chatTitle: PropTypes.string,
  isLastGroup: PropTypes.bool,
};

MessageGroup.displayName = 'MessageGroup';

export default React.memo(MessageGroup);
